import { Component, Input, ChangeDetectionStrategy, inject } from '@angular/core';
import { SearchResultsRepositoryService } from '../../services/search-results/search-results-repository.service';
import { SearchResult } from '../../interfaces/search-results/search-result';
import { Order } from '../../interfaces/orders/order';
import { base64StringToBlob } from 'blob-util';
import { UboTreeNode } from '../../interfaces/hub-reports/ubo-tree-node';
import { UboTreeNodesService } from '../../services/ubo-tree-nodes/ubo-tree-nodes.service';
import { TranslocoService } from '@jsverse/transloco';

export interface DownloadSearchDocumentsLabels {
  documents: string;
  registryDocument: string;
  regHubDocument: string;
  hubReportDocument: string;
  jsonDocument: string;
}

@Component({
    selector: 'lib-download-search-documents',
    templateUrl: './download-search-documents.component.html',
    styleUrls: ['./download-search-documents.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DownloadSearchDocumentsComponent {
  private readonly transloco = inject(TranslocoService);
    isSaving: boolean = false;

    @Input({ required: true }) order!: Order;
    @Input() uboTreeNode?: UboTreeNode;

    @Input() buttonVariant: 'flat' | 'stroked' = 'flat';

    @Input() hideRegHubDocument: boolean = false;
    @Input() hideResultDocument: boolean = false;
    @Input() showParsedResultsJson: boolean = false;

    private _labels?: Partial<DownloadSearchDocumentsLabels>;
    @Input() set labels(value: Partial<DownloadSearchDocumentsLabels> | null | undefined) { this._labels = value ?? undefined; }
    /** Caller-provided labels override the translated defaults. */
    get labels(): DownloadSearchDocumentsLabels {
      const defaults: DownloadSearchDocumentsLabels = {
        documents: this.transloco.translate('downloadDocuments.documents'),
        registryDocument: this.transloco.translate('downloadDocuments.registryDocument'),
        regHubDocument: this.transloco.translate('downloadDocuments.regHubDocument'),
        hubReportDocument: this.transloco.translate('downloadDocuments.hubReportDocument'),
        jsonDocument: this.transloco.translate('downloadDocuments.jsonDocument2'),
      };
      return { ...defaults, ...this._labels } as DownloadSearchDocumentsLabels;
    }

    searchResult?: SearchResult | null = null;

    constructor(
        private searchResultsRepo: SearchResultsRepositoryService,
        private uboTreeService: UboTreeNodesService) { }

    ngOnInit(): void {
        this.searchResult = this.order.searchResult;
    }

    hasResult(): boolean {
        return !!this.searchResult;
    }

    hasResultDocument(): boolean {
        return !!this.searchResult?.document;
    }

    hasRegHubDocument(): boolean {
        return !!this.searchResult?.summaryDocument;
    }

    isComplete(): boolean {
        return (this.order.orderStatusTypeID == "Complete")
    }

    openResultDocument() {
        this.searchResultsRepo.getSearchResultDocument(this.order.id)
            .subscribe(result => {
                var mimeType = "application/pdf";
                switch (result.documentTypeID) {
                    case "Text":
                        mimeType = "text/plain";
                        break;
                    default:
                        mimeType = "application/pdf";
                }

                const blob = base64StringToBlob(result.base64, mimeType);
                const blobURL = URL.createObjectURL(blob);
                // Open the document in a new tab instead of downloading
                window.open(blobURL, '_blank');
            });
    }

    openRegHubDocument() {
        this.searchResultsRepo.getSearchResultSummaryDocument(this.order.id)
            .subscribe(result => {
                const blob = base64StringToBlob(result.base64, "application/pdf");
                const blobURL = URL.createObjectURL(blob);
                // Open the document in a new tab instead of downloading
                window.open(blobURL, '_blank');
            });
    }

    openHubReportDocument() {
        if(!this.uboTreeNode?.document) {
            return;
        }

        this.uboTreeService.getNodeDocument(this.uboTreeNode.id, this.uboTreeNode.document).subscribe(doc => {
            const blobUrl = URL.createObjectURL(doc);
            window.open(blobUrl, '_blank');
        });
    }

    downloadParsedResult() {
    //Get Document
        this.searchResultsRepo.getParsedResults(this.order.id)
        .subscribe(result => {
            const documentType = `application/json" }`.toLowerCase();
            const blob = new Blob([JSON.stringify(result)], { type: documentType });
            const blobURL = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = `${this.order.referenceNumber}-ParsedResult.json`; // set a name for the file
            link.click();
        });
    }
}
